<script setup lang="ts">
import IconOpenInNew from '~icons/material-symbols/open-in-new'
import IconWarning from '~icons/material-symbols/warning-outline'

const props = withDefaults(defineProps<{
  projectId: string
  modelValue: boolean
  disabled?: boolean
  // Terminal financing: blocks the store link (entry point to edit the kit)
  readOnly?: boolean
}>(), {
  readOnly: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const { t } = useI18n()

const STORE_URL = 'https://loja.solfacil.com.br/spare-products'

const isEnabled = computed({
  get: () => props.modelValue ?? false,
  set: (value: boolean) => {
    emit('update:modelValue', value)
  },
})

const switchLabel = computed(() => (isEnabled.value ? t('simulation.solfacil_smart.form.yes') : t('simulation.solfacil_smart.form.no')))

function openStore() {
  if (props.readOnly)
    return

  window.open(STORE_URL, '_blank')
}

function handleSwitchChange(value: any) {
  if (typeof value === 'boolean') {
    isEnabled.value = value
  }
  else if (value?.target) {
    const target = value.target as HTMLInputElement
    isEnabled.value = target.checked
  }
  else {
    isEnabled.value = !isEnabled.value
  }
}
</script>

<template>
  <div class="solfacil-smart-form">
    <div class="content">
      <div class="header">
        <h2 class="title">
          {{ t('simulation.solfacil_smart.form.title') }}
        </h2>
      </div>

      <p class="subtitle">
        {{ t('simulation.solfacil_smart.form.subtitle') }}
      </p>

      <div class="box-container">
        <div class="switch">
          <SolSwitch
            id="solfacil-smart-switch"
            :label="switchLabel"
            text-direction="right"
            :checked="isEnabled"
            :disabled="disabled"
            name="solfacil-smart"
            value="solfacil-smart"
            @change="handleSwitchChange"
          />
        </div>

        <div class="link">
          <a
            id="link-solfacil-smart-store"
            class="store-link"
            :class="{ 'pointer-events-none opacity-60': readOnly }"
            :aria-disabled="readOnly || undefined"
            @click="openStore"
          >
            {{ t('simulation.solfacil_smart.form.store_link') }}
            <IconOpenInNew class="link-icon" />
          </a>
        </div>
      </div>

      <div
        v-if="isEnabled"
        class="warning-container"
      >
        <div class="warning-content">
          <IconWarning class="warning-icon" />
          <p class="warning-text">
            {{ t('simulation.solfacil_smart.form.warning') }}
          </p>
        </div>
      </div>
    </div>

    <div class="image">
      <img
        src="/images/solfacil-smart-illustration.svg"
        :alt="t('simulation.solfacil_smart.image_alt')"
        class="illustration-img"
      >
    </div>
  </div>
</template>

<style lang="scss" scoped>
.solfacil-smart-form {
  @apply flex flex-col md:system:flex-row items-start md:system:items-center;
  @apply bg-neutral-high-pure p-6 rounded-lg;
  @apply gap-4xs md:system:gap-8;
  @apply relative overflow-hidden;
  @apply mb-6;
  @apply border border-neutral-high-medium;

  .content {
    @apply flex-1 flex flex-col;

    .header {
      @apply flex items-center gap-1.5 flex-wrap mb-1;

      .title {
        @apply fonts-heading-h4 text-neutral-low-dark mb-0;
      }
    }

    .subtitle {
      @apply text-3xs text-neutral-low-light mb-4 mx-0;
    }

    .box-container {
      @apply flex md:system:items-center gap-2 md:system:gap-8 flex-col md:system:flex-row mt-4 md:system:mt-0;

      .switch {
        @apply flex items-center gap-3 mb-4 md:system:mb-0;
      }

      .link {
        .store-link {
          @apply flex items-center gap-1.5;
          @apply fonts-body-medium-regular text-neutral-low-medium;
          @apply cursor-pointer hover:text-neutral-low-dark underline;
          @apply transition-colors;

          .link-icon {
            @apply w-4 h-4;
          }
        }
      }
    }

    .warning-container {
      .warning-content {
        @apply flex items-center gap-2 mt-5;

        .warning-icon {
          @apply w-4 h-4 flex-shrink-0 text-feedback-warning-pure;
        }

        .warning-text {
          @apply text-feedback-warning-dark font-bold text-micro;
        }

        p {
          @apply m-0;
        }
      }
    }
  }

  .image {
    @apply absolute right-0 bottom-0;
    @apply hidden md:system:block;

    .illustration-img {
      @apply w-full max-w-[130px] h-auto;
      @apply object-contain;
    }
  }
}
</style>
